<script setup lang="ts">
import type { Deal } from '#shared/types/deal'

definePageMeta({ layout: 'default' })

const route = useRoute()
const slug = computed(() => route.params.slug as string)
const config = useRuntimeConfig()
const { trackEvent } = useUmami()
const { user, isAdmin } = useAuth()
const api = useApi()
const pageUrl = computed(() => `${config.public.appUrl}${route.path}`)
const defaultShareImage = `${config.public.appUrl}/og-default.png`

// ── Oferta ──────────────────────────────────────────────────────────────────
type DealDetail = Deal & {
  netScore?: number
}

const { data: deal, status, refresh: refreshDeal } = await useFetch<DealDetail>(`/deals/${slug.value}`, {
  baseURL: config.public.apiBaseUrl,
  key: `deal-${slug.value}`,
})

if (!deal.value && status.value === 'success') {
  throw createError({ status: 404, statusText: 'Oferta não encontrada' })
}

const seoDescription = computed(() => {
  if (!deal.value) return undefined

  const rawDescription = deal.value.description?.replace(/\s+/g, ' ').trim()
  if (rawDescription) {
    return rawDescription.slice(0, 155)
  }

  const parts = [
    `Oferta na ${deal.value.store.name}`,
    deal.value.category.name,
    deal.value.price ? `por ${deal.value.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : undefined,
  ].filter(Boolean)

  return `${parts.join(' • ')} no Descontito.`
})

const seoTitle = computed(() => {
  if (!deal.value) return 'Oferta — Descontito'
  return `${deal.value.title} — ${deal.value.store.name} | Descontito`
})

const seoImage = computed(() => deal.value?.imageUrl ?? undefined)
const resolvedSeoImage = computed(() => seoImage.value ?? defaultShareImage)

function truncateStructuredText(value: string, maxLength: number) {
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (normalized.length <= maxLength) return normalized
  return `${normalized.slice(0, maxLength - 3).trim()}...`
}

const structuredProductName = computed(() => {
  if (!deal.value) return undefined
  return truncateStructuredText(deal.value.title, 150)
})

// SEO
useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
  ogTitle: seoTitle,
  ogDescription: seoDescription,
  ogImage: resolvedSeoImage,
  ogImageAlt: computed(() => deal.value?.title ?? 'Oferta no Descontito'),
  ogType: 'website',
  ogUrl: pageUrl,
  twitterTitle: seoTitle,
  twitterDescription: seoDescription,
  twitterImage: resolvedSeoImage,
  twitterImageAlt: computed(() => deal.value?.title ?? 'Oferta no Descontito'),
  twitterCard: 'summary_large_image',
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: pageUrl.value,
    },
  ],
  meta: [
    { property: 'og:image:secure_url', content: resolvedSeoImage.value },
    { property: 'product:price:amount', content: deal.value?.price?.toString() ?? '' },
    { property: 'product:price:currency', content: 'BRL' },
    { property: 'product:availability', content: deal.value?.status === 'ACTIVE' ? 'in stock' : 'out of stock' },
  ],
  script: deal.value
    ? [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: structuredProductName.value,
            description: seoDescription.value,
            image: deal.value.imageUrl ? [deal.value.imageUrl] : undefined,
            category: deal.value.category.name,
            brand: {
              '@type': 'Brand',
              name: deal.value.store.name,
            },
            offers: {
              '@type': 'AggregateOffer',
              offerCount: 1,
              lowPrice: deal.value.price,
              highPrice: deal.value.price,
              priceCurrency: 'BRL',
              url: pageUrl.value,
            },
          }),
        },
      ]
    : [],
})

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: computed(() => [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: config.public.appUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: deal.value?.category.name ?? 'Categoria',
        item: deal.value ? `${config.public.appUrl}/categoria/${deal.value.category.slug}` : `${config.public.appUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: deal.value?.title ?? 'Oferta',
        item: pageUrl.value,
      },
    ]),
  }),
])

// Tracking de visualização
onMounted(() => {
  if (deal.value) {
    trackEvent('deal-view', {
      dealId: deal.value.id,
      slug: deal.value.slug,
      store: deal.value.store.name,
      category: deal.value.category.name,
    })
  }
})

const ctaUrl = computed(() =>
  deal.value?.shortCode
    ? `${config.public.shortLinkBase}/${deal.value.shortCode}`
    : deal.value?.url ?? '#',
)

const shareData = computed(() => ({
  title: deal.value?.title ?? 'Oferta no Descontito',
  text: deal.value
    ? `${deal.value.title} por ${deal.value.price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ?? 'um preco especial'} no Descontito.`
    : 'Confira esta oferta no Descontito.',
  url: pageUrl.value,
}))

const shareMessage = computed(() => `${shareData.value.text} ${shareData.value.url}`)

function handleCtaClick() {
  if (!deal.value) return
  trackEvent('deal-click', {
    dealId: deal.value.id,
    slug: deal.value.slug,
    store: deal.value.store.name,
    price: deal.value.price ?? 0,
  })
}

const shareLoading = ref(false)
const shareCopied = ref(false)
const shareModalOpen = ref(false)
const supportsNativeShare = ref(false)

async function handleShare() {
  if (!deal.value || shareLoading.value) return

  shareLoading.value = true

  try {
    if (supportsNativeShare.value && navigator.share && (!navigator.canShare || navigator.canShare(shareData.value))) {
      await navigator.share(shareData.value)
      trackEvent('deal-share', {
        dealId: deal.value.id,
        slug: deal.value.slug,
        store: deal.value.store.name,
      })
      return
    }

    shareModalOpen.value = true
    trackEvent('deal-share-open', {
      dealId: deal.value.id,
      slug: deal.value.slug,
      source: 'modal',
    })
  }
  catch {
    shareModalOpen.value = true
  }
  finally {
    shareLoading.value = false
  }
}

async function copyShareLink() {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(shareData.value.url)
  }
  else {
    window.prompt('Copie o link da oferta:', shareData.value.url)
  }

  shareCopied.value = true
  shareModalOpen.value = false
  if (deal.value) {
    trackEvent('deal-share', {
      dealId: deal.value.id,
      slug: deal.value.slug,
      channel: 'copy-link',
    })
  }
  setTimeout(() => { shareCopied.value = false }, 2000)
}

function trackShareTarget(channel: string) {
  if (!deal.value) return
  trackEvent('deal-share', {
    dealId: deal.value.id,
    slug: deal.value.slug,
    channel,
  })
}

const shareTargets = computed(() => {
  const encodedUrl = encodeURIComponent(shareData.value.url)
  const encodedText = encodeURIComponent(shareMessage.value)

  return [
    {
      label: 'WhatsApp',
      description: 'Enviar a oferta em uma conversa.',
      icon: 'i-heroicons-chat-bubble-left-right',
      href: `https://wa.me/?text=${encodedText}`,
    },
    {
      label: 'Facebook',
      description: 'Publicar ou compartilhar no Facebook.',
      icon: 'i-heroicons-users',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      label: 'X',
      description: 'Postar no X com o link da oferta.',
      icon: 'i-heroicons-megaphone',
      href: `https://twitter.com/intent/tweet?text=${encodedText}`,
    },
  ]
})

// Copiar cupom
const couponCopied = ref(false)
function copyCoupon() {
  if (!deal.value?.couponCode) return
  navigator.clipboard.writeText(deal.value.couponCode)
  couponCopied.value = true
  trackEvent('coupon-copy', {
    dealId: deal.value.id,
    store: deal.value.store.name,
    source: 'deal-page',
  })
  setTimeout(() => { couponCopied.value = false }, 2000)
}

// ── Votos ────────────────────────────────────────────────────────────────────
const { userVote, voteCount, loading: voteLoading, vote } = useVote(deal.value?.id ?? '')

// Temperatura reativa — atualiza após voto
const currentTemperature = ref(deal.value?.temperature ?? 0)
watch(() => deal.value?.temperature, (v) => { if (v != null) currentTemperature.value = v })

onMounted(() => {
  if (deal.value) {
    voteCount.value = deal.value.netScore ?? 0
    userVote.value = deal.value.userVote ?? null
    currentTemperature.value = deal.value.temperature ?? 0
  }

  supportsNativeShare.value = typeof navigator !== 'undefined' && typeof navigator.share === 'function'
})

async function handleVote(type: 'UP' | 'DOWN') {
  await vote(type)
  // Atualiza temperatura após voto
  await refreshDeal()
}

// ── Comentários ──────────────────────────────────────────────────────────────
interface Comment {
  id: string
  content: string
  createdAt: string
  user: { id: string; name: string; image: string | null }
  children: Comment[]
}

// server: false → sempre client-side, nunca stale do cache ISR
// Comentários são conteúdo dinâmico — não devem ser cacheados com a página
const { data: comments, refresh: refreshComments } = useFetch<Comment[]>(
  () => `/deals/${deal.value?.id}/comments`,
  {
    baseURL: config.public.apiBaseUrl,
    key: `comments-${deal.value?.id}`,
    default: () => [],
    server: false,
  },
)

// Contagem total incluindo respostas (replies)
const totalComments = computed(() =>
  (comments.value ?? []).reduce((sum, c) => sum + 1 + (c.children?.length ?? 0), 0),
)

const newComment = ref('')
const replyingTo = ref<string | null>(null)
const replyContent = ref('')
const commentLoading = ref(false)

// Emoji picker
const showEmojiPicker = ref(false)
const commentEditorRef = ref<HTMLElement | null>(null)

const EMOJIS = [
  '😀','😂','🥰','😍','😎','🤔','😅','😭','🤩','😤',
  '🙄','😱','🤯','😏','🥳','🤗','😬','🫡','💀','🫠',
  '👍','👎','💪','🙏','👏','🤝','🫶','✌️','🤌','👌',
  '❤️','🔥','💯','⭐','✅','❌','🎉','🏆','💥','⚡',
  '💸','🛒','💰','🤑','📦','🎁','🏷️','🚀','📉','📈',
]

function insertEmoji(emoji: string) {
  newComment.value += emoji
  showEmojiPicker.value = false
}

function insertReplyEmoji(emoji: string) {
  replyContent.value += emoji
  showReplyEmojiPicker.value = false
}

const showReplyEmojiPicker = ref(false)

async function submitComment() {
  if (!newComment.value.trim() || !deal.value) return
  commentLoading.value = true
  try {
    await api(`/deals/${deal.value.id}/comments`, {
      method: 'POST',
      body: { content: newComment.value.trim() },
    })
    newComment.value = ''
    showEmojiPicker.value = false
    await refreshComments()
    trackEvent('comment-create', { dealId: deal.value.id, type: 'comment' })
  }
  catch {
    // Falha nao bloqueante ao publicar comentario.
  }
  finally { commentLoading.value = false }
}

async function submitReply(parentId: string) {
  if (!replyContent.value.trim() || !deal.value) return
  commentLoading.value = true
  try {
    await api(`/deals/${deal.value.id}/comments`, {
      method: 'POST',
      body: { content: replyContent.value.trim(), parentId },
    })
    replyContent.value = ''
    replyingTo.value = null
    showReplyEmojiPicker.value = false
    await refreshComments()
    trackEvent('comment-create', { dealId: deal.value.id, type: 'reply' })
  }
  catch {
    // Falha nao bloqueante ao publicar resposta.
  }
  finally { commentLoading.value = false }
}

async function deleteComment(commentId: string) {
  if (!deal.value) return
  try {
    await api(`/deals/${deal.value.id}/comments/${commentId}`, { method: 'DELETE' })
    await refreshComments()
  }
  catch {
    // Falha nao bloqueante ao excluir comentario.
  }
}

function timeAgo(dateStr: string) {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000 / 60)
  if (diff < 1) return 'agora'
  if (diff < 60) return `${diff}min`
  if (diff < 60 * 24) return `${Math.floor(diff / 60)}h`
  return `${Math.floor(diff / 60 / 24)}d`
}

const canDelete = (commentUserId: string) => isAdmin.value || user.value?.id === commentUserId

// ── Favoritos ─────────────────────────────────────────────────────────────────
const isFavorited = ref(false)
const favoriteLoading = ref(false)

async function loadFavoriteStatus() {
  if (!user.value || !deal.value) return
  try {
    const res = await api<{ isFavorited: boolean }>(`/favorites/check/${deal.value.id}`)
    isFavorited.value = res.isFavorited
  }
  catch {
    // Falha nao bloqueante ao carregar favorito.
  }
}

async function toggleFavorite() {
  if (!user.value) {
    trackEvent('favorite-login-required', { dealId: deal.value?.id ?? '', source: 'deal-page' })
    navigateTo(`/login?redirect=${route.fullPath}`)
    return
  }
  if (!deal.value) return
  favoriteLoading.value = true
  try {
    if (isFavorited.value) {
      await api(`/favorites/${deal.value.id}`, { method: 'DELETE' })
      isFavorited.value = false
      trackEvent('favorite-remove', { dealId: deal.value.id, source: 'deal-page' })
    }
    else {
      await api(`/favorites/${deal.value.id}`, { method: 'POST' })
      isFavorited.value = true
      trackEvent('favorite-add', { dealId: deal.value.id, source: 'deal-page' })
    }
  }
  catch {
    // Falha nao bloqueante ao alternar favorito.
  }
  finally { favoriteLoading.value = false }
}

onMounted(() => { loadFavoriteStatus() })
</script>

<template>
  <div>
    <!-- Loading skeleton -->
    <div v-if="status === 'pending'" class="grid md:grid-cols-2 gap-8">
      <USkeleton class="aspect-square rounded-xl" />
      <div class="space-y-4">
        <USkeleton class="h-8 w-3/4" />
        <USkeleton class="h-6 w-1/2" />
        <USkeleton class="h-10 w-40" />
        <USkeleton class="h-12 w-full" />
      </div>
    </div>

    <!-- Detalhe da oferta -->
    <div v-else-if="deal" class="space-y-6">
      <div class="grid md:grid-cols-2 gap-5 md:gap-8">
        <!-- Imagem -->
        <div class="relative rounded-xl overflow-hidden flex items-center justify-center aspect-[5/4] sm:aspect-[4/3] md:aspect-square border border-gray-200 bg-white max-w-md md:max-w-xl mx-auto w-full">
          <NuxtImg
            v-if="deal.imageUrl"
            :src="deal.imageUrl"
            :alt="deal.title"
            class="absolute inset-0 w-full h-full object-contain p-3 md:p-6"
            width="640"
            sizes="(max-width: 768px) 100vw, 50vw"
            format="webp"
            quality="78"
            loading="eager"
            fetchpriority="high"
            preload
            decoding="async"
          />
          <UIcon v-else name="i-heroicons-photo" class="w-24 h-24 text-gray-300" />
        </div>

        <!-- Info -->
        <div class="space-y-3 min-w-0 overflow-hidden">
          <!-- Breadcrumb: Loja · Categoria -->
          <div class="flex items-center gap-2 text-sm text-gray-500">
            <NuxtLink :to="`/loja/${deal.store.slug}`" class="hover:text-primary-500 font-medium">
              {{ deal.store.name }}
            </NuxtLink>
            <span>·</span>
            <NuxtLink :to="`/categoria/${deal.category.slug}`" class="hover:text-primary-500">
              {{ deal.category.name }}
            </NuxtLink>
          </div>

          <!-- Título -->
          <h1 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white leading-tight break-words">
            {{ deal.title }}
          </h1>

          <!-- Preço -->
          <PriceTag
            :price="deal.price"
            :original-price="deal.originalPrice"
            :discount="deal.discount"
            size="lg"
          />

          <!-- Histórico de preços (produto monitorado) -->
          <div v-if="deal.product" class="flex items-center gap-2 flex-wrap">
            <UBadge
              v-if="deal.source === 'PRICE_MONITOR'"
              color="success"
              variant="soft"
              size="sm"
              title="Queda de preço confirmada pelo nosso histórico — não é desconto inflado"
            >
              📉 Desconto real
            </UBadge>
            <NuxtLink
              :to="`/produto/${deal.product.slug}`"
              class="inline-flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600"
            >
              <UIcon name="i-heroicons-chart-bar" class="w-4 h-4" />
              Ver histórico de preços e comparar lojas
            </NuxtLink>
          </div>

          <!-- CTA + Favoritar -->
          <div class="flex gap-2">
            <a :href="ctaUrl" target="_blank" rel="noopener noreferrer nofollow" class="flex-1" @click="handleCtaClick">
              <UButton color="primary" size="lg" class="w-full" icon="i-heroicons-arrow-top-right-on-square" trailing>
                Ver oferta
              </UButton>
            </a>
            <UButton
              color="neutral"
              variant="outline"
              size="lg"
              :icon="shareCopied ? 'i-heroicons-check' : 'i-heroicons-share'"
              :loading="shareLoading"
              :title="shareCopied ? 'Link copiado' : 'Compartilhar oferta'"
              @click="handleShare"
            />
            <UButton
              color="neutral"
              variant="outline"
              size="lg"
              :icon="isFavorited ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'"
              :loading="favoriteLoading"
              :title="isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'"
              :class="isFavorited ? '!text-red-500 !border-red-300 dark:!border-red-800' : 'hover:!border-red-400 hover:!text-red-500'"
              @click="toggleFavorite"
            />
          </div>

          <!-- Temperatura + Votação inline -->
          <div class="flex items-center gap-2 flex-wrap">
            <TemperatureBadge :temperature="currentTemperature" />
            <span class="text-gray-300 dark:text-gray-600 select-none">·</span>
            <div class="flex items-center gap-1.5">
              <button
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all"
                :class="userVote === 'UP'
                  ? 'bg-primary-500 border-primary-500 text-white'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-primary-400 hover:text-primary-500'"
                :disabled="voteLoading"
                @click="handleVote('UP')"
              >
                🚀 Hype
              </button>
              <span
                class="text-xs font-bold px-1.5 tabular-nums"
                :class="voteCount > 0 ? 'text-primary-500' : voteCount < 0 ? 'text-red-500' : 'text-gray-400'"
              >
                {{ voteCount > 0 ? '+' : '' }}{{ voteCount }}°
              </span>
              <button
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all"
                :class="userVote === 'DOWN'
                  ? 'bg-red-500 border-red-500 text-white'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-red-400 hover:text-red-500'"
                :disabled="voteLoading"
                @click="handleVote('DOWN')"
              >
                📉 Flopp
              </button>
            </div>
          </div>

          <div class="flex items-center gap-2 flex-wrap">
            <UBadge v-if="deal.freeShipping" color="success" size="md" variant="soft">
              ✓ Frete Grátis
            </UBadge>

            <div v-if="deal.couponCode" class="flex items-center gap-2">
              <span class="text-sm text-gray-600 dark:text-gray-400">Cupom:</span>
              <UButton
                :color="couponCopied ? 'success' : 'primary'"
                variant="outline"
                :icon="couponCopied ? 'i-heroicons-check' : 'i-heroicons-clipboard'"
                size="sm"
                class="font-mono tracking-widest"
                @click="copyCoupon"
              >
                {{ couponCopied ? 'Copiado!' : deal.couponCode }}
              </UButton>
            </div>
          </div>

          <!-- Expiração -->
          <p v-if="deal.expiresAt" class="text-xs text-gray-400">
            Oferta válida até {{ new Date(deal.expiresAt).toLocaleDateString('pt-BR') }}
          </p>

          <!-- Descrição -->
          <div v-if="deal.description" class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h2 class="font-semibold text-gray-900 dark:text-white mb-2">Descrição</h2>
            <p class="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{{ deal.description }}</p>
          </div>
        </div>
      </div>

      <!-- ── Comentários ─────────────────────────────────────────────────── -->
      <div class="border-t border-gray-200 dark:border-gray-700 pt-8">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-6">
          Comentários
          <span class="text-sm font-normal text-gray-400 ml-1">({{ totalComments }})</span>
        </h2>

        <!-- Editor de novo comentário -->
        <div v-if="user" ref="commentEditorRef" class="mb-8 relative">
          <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-visible bg-white dark:bg-gray-900 shadow-sm">
            <!-- Barra de ferramentas -->
            <div class="flex items-center gap-1 px-3 pt-2 pb-1.5 border-b border-gray-100 dark:border-gray-800">
              <div class="relative">
                <button
                  type="button"
                  class="p-1.5 rounded-lg text-gray-400 hover:text-yellow-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-base leading-none"
                  title="Inserir emoji"
                  @click="showEmojiPicker = !showEmojiPicker"
                >
                  😊
                </button>
                <!-- Emoji Picker -->
                <div
                  v-if="showEmojiPicker"
                  class="absolute left-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 w-72"
                >
                  <div class="grid grid-cols-10 gap-0.5">
                    <button
                      v-for="emoji in EMOJIS"
                      :key="emoji"
                      type="button"
                      class="w-7 h-7 flex items-center justify-center text-lg rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      @click="insertEmoji(emoji)"
                    >
                      {{ emoji }}
                    </button>
                  </div>
                </div>
              </div>
              <span class="text-xs text-gray-300 dark:text-gray-600 ml-1">Dica: use emojis para expressar sua opinião!</span>
            </div>

            <!-- Textarea -->
            <textarea
              v-model="newComment"
              placeholder="Compartilhe sua experiência com esta oferta..."
              class="w-full bg-transparent px-4 py-3 resize-none min-h-[120px] focus:outline-none text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400"
              maxlength="1000"
              @keydown.ctrl.enter.prevent="submitComment"
            />

            <!-- Footer -->
            <div class="flex items-center justify-between px-4 py-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <span class="text-xs text-gray-400">
                {{ newComment.length }}/1000 · Ctrl+Enter para enviar
              </span>
              <UButton
                color="primary"
                size="sm"
                :loading="commentLoading"
                :disabled="!newComment.trim()"
                @click="submitComment"
              >
                Comentar
              </UButton>
            </div>
          </div>
        </div>

        <!-- Não logado -->
        <div v-else class="mb-8 p-5 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 text-sm text-gray-500 text-center">
          <UIcon name="i-heroicons-chat-bubble-left-right" class="w-6 h-6 mx-auto mb-2 opacity-40" />
          <p>
            <NuxtLink to="/login" class="text-primary-500 hover:underline font-semibold">Entrar</NuxtLink>
            para comentar nesta oferta.
          </p>
        </div>

        <!-- Lista de comentários -->
        <div v-if="comments && comments.length > 0" class="space-y-5">
          <div v-for="comment in comments" :key="comment.id" class="space-y-3">
            <!-- Comentário principal -->
            <div class="flex gap-3">
              <UAvatar
                :src="comment.user.image ?? undefined"
                :alt="comment.user.name"
                size="md"
              />
              <div class="flex-1 min-w-0">
                <div class="bg-gray-50 dark:bg-gray-800/60 rounded-xl px-4 py-3 border border-gray-100 dark:border-gray-700/50">
                  <div class="flex items-center justify-between gap-2 mb-2">
                    <div class="flex items-center gap-2">
                      <span class="text-sm font-semibold text-gray-900 dark:text-white">{{ comment.user.name }}</span>
                      <span v-if="isAdmin && comment.user.id !== user?.id" class="text-xs px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">usuário</span>
                    </div>
                    <span class="text-xs text-gray-400 shrink-0">{{ timeAgo(comment.createdAt) }}</span>
                  </div>
                  <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{{ comment.content }}</p>
                </div>
                <!-- Ações -->
                <div class="flex items-center gap-4 mt-1.5 px-2">
                  <button
                    v-if="user"
                    class="text-xs text-gray-400 hover:text-primary-500 transition-colors font-medium"
                    @click="replyingTo = replyingTo === comment.id ? null : comment.id; replyContent = ''"
                  >
                    Responder
                  </button>
                  <button
                    v-if="canDelete(comment.user.id)"
                    class="text-xs text-gray-400 hover:text-red-500 transition-colors font-medium"
                    @click="deleteComment(comment.id)"
                  >
                    Excluir
                  </button>
                </div>

                <!-- Campo de resposta -->
                <div v-if="replyingTo === comment.id" class="mt-3 relative">
                  <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-visible bg-white dark:bg-gray-900">
                    <div class="flex items-center gap-1 px-2 pt-1.5 pb-1 border-b border-gray-100 dark:border-gray-800">
                      <div class="relative">
                        <button
                          type="button"
                          class="p-1 rounded text-gray-400 hover:text-yellow-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm"
                          @click="showReplyEmojiPicker = !showReplyEmojiPicker"
                        >
                          😊
                        </button>
                        <div
                          v-if="showReplyEmojiPicker"
                          class="absolute left-0 top-full mt-1 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-2 w-64"
                        >
                          <div class="grid grid-cols-10 gap-0.5">
                            <button
                              v-for="emoji in EMOJIS"
                              :key="emoji"
                              type="button"
                              class="w-6 h-6 flex items-center justify-center text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              @click="insertReplyEmoji(emoji)"
                            >
                              {{ emoji }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <textarea
                      v-model="replyContent"
                      :placeholder="`Responder para ${comment.user.name}...`"
                      class="w-full bg-transparent px-3 py-2 resize-none min-h-[80px] focus:outline-none text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400"
                      maxlength="500"
                      @keydown.ctrl.enter.prevent="submitReply(comment.id)"
                    />
                    <div class="flex items-center justify-end gap-2 px-3 py-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                      <UButton size="xs" color="neutral" variant="ghost" @click="replyingTo = null">
                        Cancelar
                      </UButton>
                      <UButton size="xs" color="primary" :loading="commentLoading" :disabled="!replyContent.trim()" @click="submitReply(comment.id)">
                        Responder
                      </UButton>
                    </div>
                  </div>
                </div>

                <!-- Respostas -->
                <div v-if="comment.children?.length" class="mt-3 space-y-2 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                  <div v-for="reply in comment.children" :key="reply.id" class="flex gap-3">
                    <UAvatar
                      :src="reply.user.image ?? undefined"
                      :alt="reply.user.name"
                      size="sm"
                    />
                    <div class="flex-1 min-w-0">
                      <div class="bg-gray-50 dark:bg-gray-800/60 rounded-xl px-3 py-2.5 border border-gray-100 dark:border-gray-700/50">
                        <div class="flex items-center justify-between gap-2 mb-1">
                          <span class="text-xs font-semibold text-gray-900 dark:text-white">{{ reply.user.name }}</span>
                          <span class="text-xs text-gray-400 shrink-0">{{ timeAgo(reply.createdAt) }}</span>
                        </div>
                        <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{{ reply.content }}</p>
                      </div>
                      <button
                        v-if="canDelete(reply.user.id)"
                        class="text-xs text-gray-400 hover:text-red-500 transition-colors mt-1 px-1 font-medium"
                        @click="deleteComment(reply.id)"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="text-center py-12 text-gray-400">
          <UIcon name="i-heroicons-chat-bubble-left-ellipsis" class="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p class="text-sm font-medium">Seja o primeiro a comentar!</p>
          <p class="text-xs mt-1 opacity-70">Compartilhe sua experiência com esta oferta.</p>
        </div>
      </div>
    </div>

    <!-- Oferta não encontrada -->
    <div v-else class="text-center py-20">
      <UIcon name="i-heroicons-tag" class="w-16 h-16 mx-auto mb-4 text-gray-300" />
      <h2 class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Oferta não encontrada</h2>
      <p class="text-gray-500 mb-6">Esta oferta pode ter expirado ou sido removida.</p>
      <UButton to="/" color="primary">Ver outras ofertas</UButton>
    </div>

    <UModal v-model:open="shareModalOpen">
      <template #content>
        <UCard>
          <template #header>
            <div>
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Compartilhar oferta</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                No iPhone em rede local, o compartilhamento nativo pode não abrir sem HTTPS. Use uma das opções abaixo.
              </p>
            </div>
          </template>

          <div class="space-y-2">
            <a
              v-for="target in shareTargets"
              :key="target.label"
              :href="target.href"
              target="_blank"
              rel="noopener noreferrer"
              class="block"
              @click="trackShareTarget(target.label.toLowerCase())"
            >
              <UButton color="neutral" variant="outline" class="w-full justify-start" :icon="target.icon">
                <span class="flex flex-col items-start">
                  <span>{{ target.label }}</span>
                  <span class="text-xs text-gray-500 dark:text-gray-400">{{ target.description }}</span>
                </span>
              </UButton>
            </a>
          </div>

          <template #footer>
            <div class="flex justify-end gap-2">
              <UButton color="neutral" variant="ghost" @click="shareModalOpen = false">
                Fechar
              </UButton>
              <UButton color="primary" icon="i-heroicons-link" @click="copyShareLink">
                Copiar link
              </UButton>
            </div>
          </template>
        </UCard>
      </template>
    </UModal>
  </div>
</template>
